import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Stack,
  Badge,
  Divider,
  Avatar,
  MenuItem
} from '@mui/material';
import {
  Settings,
  Security,
  Pause,
  PlayArrow,
  Block,
  CheckCircle,
  Warning,
  AdminPanelSettings,
  Tune,
  Save,
  Refresh,
  MonetizationOn,
  TrendingUp,
  VerifiedUser,
  Pending,
  Close,
  Visibility,
  Download,
  Person,
  Assignment
} from '@mui/icons-material';
import { BrowserProvider, Contract, parseEther, formatEther } from 'ethers';
import { useKYC } from '../../context/KYCProvider';

// Import contract ABIs
import marketplaceContract from '../abis/CarbonCreditMarketplace.json';
import nftContract from '../abis/CarbonCreditNFT.json';
import vaultContract from '../abis/PlatformVault.json';

const MARKETPLACE_ADDRESS = marketplaceContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;
const NFT_ADDRESS = nftContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;
const VAULT_ADDRESS = vaultContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;

const AdminPlatformManagement = ({
  account,
  isAdmin,
  provider,
  signer,
  vault,
  nft,
  carbon,
  marketplace,
  vaultAddress,
  nftAddress,
  carbonAddress,
  marketplaceAddress,
  history,
  historyAll,
  retiredEvents,
  retiredEventsAll,
  enqueueSnackbar,
  refreshVaultStatus,
  initialTab // <-- add this prop
}) => {
  const [loading, setLoading] = useState(true);;
  const [activeTab, setActiveTab] = useState(initialTab || 0); // use initialTab if provided
  const [contracts, setContracts] = useState({});
  const [historyScope, setHistoryScope] = useState('my'); // 'my' | 'all'
  
  // KYC context
  const { 
    applications, 
    loadKYCApplication, 
    loadApplicationDocument, 
    updateApplicationStatus,
    getApplicationsByStatus,
    getApplicationStats 
  } = useKYC();
  
  // Platform settings state
  const [platformSettings, setPlatformSettings] = useState({
    marketplaceFee: '2.5',
    royaltyFee: '2.5',
    isPaused: false,
    minListingPrice: '0.001',
    maxAuctionDuration: '7',
    emergencyStop: false
  });

  // User management state
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyAddress, setVerifyAddress] = useState('');
  const [verifyAction, setVerifyAction] = useState(true);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Pricing dialogs
  const [updateCarbonPriceDialogOpen, setUpdateCarbonPriceDialogOpen] = useState(false);
  const [updateErgonPriceDialogOpen, setUpdateErgonPriceDialogOpen] = useState(false);
  const [carbonPriceInput, setCarbonPriceInput] = useState('');
  const [ergonPriceInput, setErgonPriceInput] = useState('');

  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Contract management state
  const [contractActions, setContractActions] = useState({
    upgrading: false,
    pausing: false,
    withdrawing: false
  });

  // KYC Application state
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  const [loadingApplication, setLoadingApplication] = useState(false);
  const [kycStatusFilter, setKycStatusFilter] = useState('all');

  useEffect(() => {
    initializeContracts();
  }, []);

  useEffect(() => {
    if (typeof initialTab === 'number') setActiveTab(initialTab);
  }, [initialTab]);

  const initializeContracts = async () => {
    setLoading(true);
    try {
      if (!window.ethereum) {
        throw new Error('No wallet connected');
      }

      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const userAccount = await signer.getAddress();
      // Note: account is passed via props, no need to set local state

      // Initialize contracts
      const marketplace = new Contract(MARKETPLACE_ADDRESS, marketplaceContract.abi, signer);
      const nft = new Contract(NFT_ADDRESS, nftContract.abi, signer);
      const vault = VAULT_ADDRESS ? new Contract(VAULT_ADDRESS, vaultContract.abi, signer) : null;

      setContracts({ marketplace, nft, vault });

      // Note: isAdmin/isOwner is passed via props, no need to check ownership here

      // Fetch current platform settings
      await fetchPlatformSettings(marketplace, nft);
      
      // Fetch user data
      await fetchUsersData(marketplace, nft);

    } catch (error) {
      console.error('Error initializing contracts:', error);
      showNotification('Error connecting to contracts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformSettings = async (marketplace, nft) => {
    try {
      // Fetch marketplace settings
      const isPaused = await marketplace.paused?.() || false;
      
      // These would need to be implemented in your contracts
      // For now, using default values
      setPlatformSettings(prev => ({
        ...prev,
        isPaused,
        // Add more contract state fetching here
      }));

    } catch (error) {
      console.error('Error fetching platform settings:', error);
    }
  };

  const fetchUsersData = async (marketplace, nft) => {
    try {
      // Fetch recent user activity from events
      const [purchaseEvents, listingEvents] = await Promise.all([
        marketplace.queryFilter(marketplace.filters.TokenPurchased(null)),
        marketplace.queryFilter(marketplace.filters.TokenListed(null))
      ]);

      const userMap = new Map();

      // Process purchase events
      purchaseEvents.forEach(event => {
        const buyer = event.args.buyer;
        if (!userMap.has(buyer)) {
          userMap.set(buyer, {
            address: buyer,
            purchases: 0,
            listings: 0,
            totalSpent: 0,
            lastActivity: 0,
            status: 'active'
          });
        }
        const user = userMap.get(buyer);
        user.purchases++;
        user.totalSpent += parseFloat(formatEther(event.args.price || '0'));
        user.lastActivity = Math.max(user.lastActivity, event.blockNumber);
      });

      // Process listing events
      listingEvents.forEach(event => {
        const seller = event.args.seller;
        if (!userMap.has(seller)) {
          userMap.set(seller, {
            address: seller,
            purchases: 0,
            listings: 0,
            totalSpent: 0,
            lastActivity: 0,
            status: 'active'
          });
        }
        const user = userMap.get(seller);
        user.listings++;
        user.lastActivity = Math.max(user.lastActivity, event.blockNumber);
      });

      setUsers(Array.from(userMap.values()).slice(0, 50)); // Limit to 50 users

    } catch (error) {
      console.error('Error fetching users data:', error);
    }
  };

  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleUpdatePlatformSettings = async () => {
    if (!isAdmin || !contracts.marketplace) {
      showNotification('Unauthorized: Only contract owner can update settings', 'error');
      return;
    }

    try {
      setLoading(true);
      
      // Example: Update marketplace fee (would need contract function)
      // await contracts.marketplace.setMarketplaceFee(parseEther(platformSettings.marketplaceFee));
      
      showNotification('Platform settings updated successfully', 'success');
      
    } catch (error) {
      console.error('Error updating platform settings:', error);
      showNotification(`Error updating settings: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseContract = async () => {
    if (!isAdmin || !contracts.marketplace) {
      showNotification('Unauthorized: Only contract owner can pause contracts', 'error');
      return;
    }

    try {
      setContractActions(prev => ({ ...prev, pausing: true }));
      
      if (platformSettings.isPaused) {
        await contracts.marketplace.unpause();
        showNotification('Contract unpaused successfully', 'success');
      } else {
        await contracts.marketplace.pause();
        showNotification('Contract paused successfully', 'warning');
      }
      
      setPlatformSettings(prev => ({ ...prev, isPaused: !prev.isPaused }));
      
    } catch (error) {
      console.error('Error toggling contract pause:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setContractActions(prev => ({ ...prev, pausing: false }));
    }
  };

  const handleWithdrawFees = async () => {
    if (!isAdmin || !contracts.vault) {
      showNotification('Unauthorized or vault not available', 'error');
      return;
    }

    try {
      setContractActions(prev => ({ ...prev, withdrawing: true }));
      
      // Example withdrawal (would need proper vault function)
      // await contracts.vault.withdrawFees();
      
      showNotification('Fees withdrawn successfully', 'success');
      
    } catch (error) {
      console.error('Error withdrawing fees:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setContractActions(prev => ({ ...prev, withdrawing: false }));
    }
  };

  const handleUserAction = (user, action) => {
    setSelectedUser({ ...user, action });
    setUserDialogOpen(true);
  };

  const executeUserAction = async () => {
    if (!selectedUser || !isAdmin) return;

    try {
      // Implement user actions like banning, verification, etc.
      // This would require additional contract functions
      
      showNotification(`User ${selectedUser.action} completed`, 'success');
      setUserDialogOpen(false);
      setSelectedUser(null);
      
    } catch (error) {
      console.error('Error executing user action:', error);
      showNotification(`Error: ${error.message}`, 'error');
    }
  };

  // Real contract functions
  const verifyUsers = async (users, status) => {
    if (!vault) {
      enqueueSnackbar?.('Vault contract not loaded', { variant: 'error' });
      return;
    }
    
    console.log(`Setting verification status for users:`, users, `to:`, status);
    
    try {
      // Estimate gas first
      const gasEstimate = await vault.setVerifiedUsers.estimateGas(users, status);
      console.log("Gas estimate:", gasEstimate.toString());
      
      // Add 20% buffer to gas estimate
      const gasLimit = Math.floor(Number(gasEstimate) * 1.2);
      
      // Wait for transaction to be mined with explicit gas limit
      const tx = await vault.setVerifiedUsers(users, status, {
        gasLimit: gasLimit
      });
      console.log("Verification transaction sent:", tx.hash);
      
      // Wait for confirmation
      const receipt = await tx.wait();
      console.log("Verification transaction confirmed:", receipt.transactionHash);
      
      return receipt;
    } catch (error) {
      console.error("Verification transaction failed:", error);
      
      // Check if it's a gas estimation error
      if (error.reason && error.reason.includes("gas")) {
        throw new Error("Transaction failed due to insufficient gas. Please check your wallet balance and try again.");
      }
      
      // Check if it's a contract revert
      if (error.reason) {
        throw new Error(`Contract error: ${error.reason}`);
      }
      
      throw error;
    }
  };

  const pause = async () => {
    if (!vault) {
      enqueueSnackbar?.('Vault contract not loaded', { variant: 'error' });
      return;
    }
    await vault.pause();
    enqueueSnackbar?.('Vault paused successfully', { variant: 'success' });
  };

  const unpause = async () => {
    if (!vault) {
      enqueueSnackbar?.('Vault contract not loaded', { variant: 'error' });
      return;
    }
    await vault.unpause();
    enqueueSnackbar?.('Vault unpaused successfully', { variant: 'success' });
  };

  // Pause/Unpause Marketplace
  const pauseMarketplace = async () => {
    if (!marketplace) return;
    try {
      await marketplace.pause();
      enqueueSnackbar?.('Marketplace paused', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar?.('Failed to pause marketplace: ' + (err.reason || err.message), { variant: 'error' });
    }
  };

  const unpauseMarketplace = async () => {
    if (!marketplace) return;
    try {
      await marketplace.unpause();
      enqueueSnackbar?.('Marketplace unpaused', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar?.('Failed to unpause marketplace: ' + (err.reason || err.message), { variant: 'error' });
    }
  };

  // Update Carbon Price
  const handleUpdateCarbonPriceConfirm = async () => {
    if (!nft) return;
    try {
      enqueueSnackbar?.('Carbon price update not implemented', { variant: 'info' });
      setUpdateCarbonPriceDialogOpen(false);
      setCarbonPriceInput('');
    } catch (err) {
      enqueueSnackbar?.('Failed to update carbon price: ' + (err.reason || err.message), { variant: 'error' });
    }
  };

  // Update Ergon Price
  const handleUpdateErgonPriceConfirm = async () => {
    if (!nft) return;
    try {
      enqueueSnackbar?.('Ergon price update not implemented', { variant: 'info' });
      setUpdateErgonPriceDialogOpen(false);
      setErgonPriceInput('');
    } catch (err) {
      enqueueSnackbar?.('Failed to update ergon price: ' + (err.reason || err.message), { variant: 'error' });
    }
  };

  // User verification handler
  const handleVerifyUser = async () => {
    if (!verifyAddress.trim()) {
      enqueueSnackbar?.('Please enter a valid address', { variant: 'error' });
      return;
    }

    setVerifyLoading(true);
    try {
      await verifyUsers([verifyAddress], verifyAction);
      enqueueSnackbar?.(`User ${verifyAction ? 'verified' : 'unverified'} successfully!`, { variant: 'success' });
      setVerifyDialogOpen(false);
      setVerifyAddress('');
      
      // Refresh vault status to reflect changes
      if (refreshVaultStatus) {
        await refreshVaultStatus();
      }
      
    } catch (err) {
      enqueueSnackbar?.('Verification failed: ' + (err.reason || err.message), { variant: 'error' });
    }
    setVerifyLoading(false);
  };

  // KYC Application handlers
  const handleViewApplication = async (application) => {
    setSelectedApplication(application);
    setLoadingApplication(true);
    setApplicationDialogOpen(true);
    
    try {
      const data = await loadKYCApplication(application.ipfsHash);
      setApplicationData(data);
    } catch (error) {
      console.error('Failed to load application:', error);
      showNotification('Failed to load application data', 'error');
    } finally {
      setLoadingApplication(false);
    }
  };

  const handleApproveApplication = async (applicationId, walletAddress) => {
    try {
      // Update KYC status
      await updateApplicationStatus(applicationId, 'approved', 'Approved by admin');
      
      // Verify user on blockchain
      await verifyUsers([walletAddress], true);
      
      showNotification('Application approved and user verified on blockchain', 'success');
      setApplicationDialogOpen(false);
      
      // Refresh vault status
      if (refreshVaultStatus) {
        await refreshVaultStatus();
      }
    } catch (error) {
      console.error('Failed to approve application:', error);
      showNotification('Failed to approve application: ' + error.message, 'error');
    }
  };

  const handleRejectApplication = async (applicationId, reason) => {
    try {
      await updateApplicationStatus(applicationId, 'rejected', reason || 'Rejected by admin');
      showNotification('Application rejected', 'info');
      setApplicationDialogOpen(false);
    } catch (error) {
      console.error('Failed to reject application:', error);
      showNotification('Failed to reject application: ' + error.message, 'error');
    }
  };

  const getFilteredKYCApplications = () => {
    if (kycStatusFilter === 'all') return applications;
    return getApplicationsByStatus(kycStatusFilter);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'under_review': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Platform Management
        </Typography>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading platform data...</Typography>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>
            You are not authorized to access the admin panel. Only contract owners can manage the platform.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Current account: {account}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Platform Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Chip
            label={`Admin: ${account.slice(0, 6)}...${account.slice(-4)}`}
            color="primary"
            icon={<AdminPanelSettings />}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Platform Status
                  </Typography>
                  <Chip
                    label={platformSettings.isPaused ? 'PAUSED' : 'ACTIVE'}
                    color={platformSettings.isPaused ? 'error' : 'success'}
                    icon={platformSettings.isPaused ? <Pause /> : <PlayArrow />}
                  />
                </Box>
                <Security color={platformSettings.isPaused ? 'error' : 'success'} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h5">
                    {users.filter(u => u.status === 'active').length}
                  </Typography>
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending KYC
                  </Typography>
                  <Typography variant="h5">
                    <Badge badgeContent={getApplicationStats().pending} color="warning" max={99}>
                      {getApplicationStats().pending}
                    </Badge>
                  </Typography>
                </Box>
                <Pending color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Verified Users
                  </Typography>
                  <Typography variant="h5">
                    {getApplicationStats().approved}
                  </Typography>
                </Box>
                <VerifiedUser color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Platform Settings" />
          <Tab label="User Management" />
          <Tab 
            label={
              <Badge badgeContent={getApplicationStats().pending} color="warning" max={99}>
                KYC Applications
              </Badge>
            } 
          />
          <Tab label="Contract Actions" />
          <Tab label="Security" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Platform Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Marketplace Fee (%)"
                  type="number"
                  value={platformSettings.marketplaceFee}
                  onChange={(e) => setPlatformSettings(prev => ({
                    ...prev,
                    marketplaceFee: e.target.value
                  }))}
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Royalty Fee (%)"
                  type="number"
                  value={platformSettings.royaltyFee}
                  onChange={(e) => setPlatformSettings(prev => ({
                    ...prev,
                    royaltyFee: e.target.value
                  }))}
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Listing Price (ETH)"
                  type="number"
                  value={platformSettings.minListingPrice}
                  onChange={(e) => setPlatformSettings(prev => ({
                    ...prev,
                    minListingPrice: e.target.value
                  }))}
                  inputProps={{ min: 0, step: 0.001 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Auction Duration (days)"
                  type="number"
                  value={platformSettings.maxAuctionDuration}
                  onChange={(e) => setPlatformSettings(prev => ({
                    ...prev,
                    maxAuctionDuration: e.target.value
                  }))}
                  inputProps={{ min: 1, max: 30 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleUpdatePlatformSettings}
                    disabled={loading}
                  >
                    Save Settings
                  </Button>
                  
                  {/* Platform Control Buttons */}
                  <Button
                    variant="outlined"
                    startIcon={<Pause />}
                    onClick={pause}
                    color="secondary"
                    disabled={!vault}
                  >
                    Pause Vault
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={unpause}
                    color="primary"
                    disabled={!vault}
                  >
                    Unpause Vault
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Pause />}
                    onClick={pauseMarketplace}
                    color="secondary"
                    disabled={!marketplace}
                  >
                    Pause Marketplace
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={unpauseMarketplace}
                    color="primary"
                    disabled={!marketplace}
                  >
                    Unpause Marketplace
                  </Button>
                </Box>
                
                {/* User Verification Section */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    User Verification
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => { setVerifyDialogOpen(true); setVerifyAction(true); }}
                      startIcon={<CheckCircle />}
                    >
                      Verify User
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => { setVerifyDialogOpen(true); setVerifyAction(false); }}
                      startIcon={<Block />}
                    >
                      Unverify User
                    </Button>
                  </Box>
                </Box>

                {/* Pricing Management Section */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Price Management
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="info"
                      onClick={() => setUpdateCarbonPriceDialogOpen(true)}
                      startIcon={<MonetizationOn />}
                    >
                      Update Carbon Price
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => setUpdateErgonPriceDialogOpen(true)}
                      startIcon={<TrendingUp />}
                    >
                      Update Ergon Price
                    </Button>
                  </Box>
                </Box>

                {/* Platform History Section */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Platform History
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Mint History
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={historyScope === 'my' ? 'Showing: My events' : 'Showing: All events'}
                        size="small"
                        color={historyScope === 'my' ? 'default' : 'primary'}
                      />
                      <Button size="small" onClick={() => setHistoryScope(s => s === 'my' ? 'all' : 'my')}>
                        {historyScope === 'my' ? 'View All' : 'View Mine'}
                      </Button>
                    </Stack>
                  </Box>
                  {(historyScope === 'my' ? (history || []) : (historyAll || [])).length === 0 ? (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                      No mint events found
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                      {(historyScope === 'my' ? history : historyAll)?.map((e, i) => (
                        <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                          Minted Token #{e.args?.tokenId?.toString()} by {e.args?.to?.slice(0, 6)}...{e.args?.to?.slice(-4)}
                          {e.timestamp && ` on ${new Date(e.timestamp * 1000).toLocaleString()}`}
                          {e.args?.burnedAmount && ` (Burned: ${e.args.burnedAmount.toString()})`}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Retirement Events
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={historyScope === 'my' ? 'Showing: My events' : 'Showing: All events'}
                        size="small"
                        color={historyScope === 'my' ? 'default' : 'primary'}
                      />
                      <Button size="small" onClick={() => setHistoryScope(s => s === 'my' ? 'all' : 'my')}>
                        {historyScope === 'my' ? 'View All' : 'View Mine'}
                      </Button>
                    </Stack>
                  </Box>
                  {(historyScope === 'my' ? (retiredEvents || []) : (retiredEventsAll || [])).length === 0 ? (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                      No retirement events found
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                      {(historyScope === 'my' ? retiredEvents : retiredEventsAll)?.map((e, i) => (
                        <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                          Token ID #{e.args?.tokenId?.toString()} retired by {e.args?.retiredBy?.slice(0, 6)}...{e.args?.retiredBy?.slice(-4)}
                          {e.timestamp && ` on ${new Date(e.timestamp * 1000).toLocaleString()}`}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Management
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Address</TableCell>
                    <TableCell>Purchases</TableCell>
                    <TableCell>Listings</TableCell>
                    <TableCell>Total Spent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow key={user.address}>
                      <TableCell>
                        {user.address.slice(0, 6)}...{user.address.slice(-4)}
                      </TableCell>
                      <TableCell>{user.purchases}</TableCell>
                      <TableCell>{user.listings}</TableCell>
                      <TableCell>{user.totalSpent.toFixed(4)} ETH</TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={user.status === 'active' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleUserAction(user, 'ban')}
                          color="error"
                        >
                          <Block />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleUserAction(user, 'verify')}
                          color="success"
                        >
                          <CheckCircle />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                KYC Applications
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Filter by Status"
                  value={kycStatusFilter}
                  onChange={(e) => setKycStatusFilter(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All Applications</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {/* KYC Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="warning.main">
                      {getApplicationStats().pending}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Pending
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="info.main">
                      {getApplicationStats().underReview}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Under Review
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="success.main">
                      {getApplicationStats().approved}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Approved
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="error.main">
                      {getApplicationStats().rejected}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Rejected
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {getFilteredKYCApplications().length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body1">
                  No KYC applications found{kycStatusFilter !== 'all' ? ` with status: ${kycStatusFilter}` : ''}.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Users can submit KYC applications from their wallet interface to get verified for platform access.
                </Typography>
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Application ID</TableCell>
                      <TableCell>Applicant</TableCell>
                      <TableCell>Wallet Address</TableCell>
                      <TableCell>Submission Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredKYCApplications().map((application) => (
                      <TableRow key={application.applicationId}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {application.applicationId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              <Person />
                            </Avatar>
                            <Typography variant="body2">
                              {application.applicantName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {application.walletAddress.slice(0, 6)}...{application.walletAddress.slice(-4)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(application.submissionDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={application.status.toUpperCase()}
                            color={getStatusColor(application.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewApplication(application)}
                              color="primary"
                              title="View Application"
                            >
                              <Visibility />
                            </IconButton>
                            {application.status === 'pending' && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleApproveApplication(application.applicationId, application.walletAddress)}
                                  color="success"
                                  title="Approve & Verify"
                                >
                                  <CheckCircle />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRejectApplication(application.applicationId)}
                                  color="error"
                                  title="Reject"
                                >
                                  <Close />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contract Actions
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Emergency Controls
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={platformSettings.isPaused}
                          onChange={handlePauseContract}
                          disabled={contractActions.pausing}
                        />
                      }
                      label="Pause All Operations"
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Temporarily halt all marketplace operations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Fee Management
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleWithdrawFees}
                      disabled={contractActions.withdrawing || !contracts.vault}
                      startIcon={contractActions.withdrawing ? <LinearProgress /> : <Settings />}
                    >
                      {contractActions.withdrawing ? 'Withdrawing...' : 'Withdraw Fees'}
                    </Button>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Withdraw accumulated platform fees
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Health
                    </Typography>
                    <Chip
                      label="All Systems Operational"
                      color="success"
                      icon={<CheckCircle />}
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Contract status and health monitoring
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Security Settings
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Security features help protect the platform from malicious activities and ensure safe operations.
            </Alert>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={platformSettings.emergencyStop}
                      onChange={(e) => setPlatformSettings(prev => ({
                        ...prev,
                        emergencyStop: e.target.checked
                      }))}
                    />
                  }
                  label="Emergency Stop Protocol"
                />
                <Typography variant="body2" color="textSecondary">
                  Immediately halt all platform operations in case of emergency
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Access Control
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Current admin: {account}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Multi-signature: Not configured
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* User Action Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)}>
        <DialogTitle>
          Confirm User Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {selectedUser?.action} user {selectedUser?.address}?
          </Typography>
          {selectedUser?.action === 'ban' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action will prevent the user from interacting with the platform.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={executeUserAction}
            color={selectedUser?.action === 'ban' ? 'error' : 'primary'}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Verification Dialog */}
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)}>
        <DialogTitle>
          {verifyAction ? 'Verify' : 'Unverify'} User
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="User Address"
            type="text"
            fullWidth
            value={verifyAddress}
            onChange={(e) => setVerifyAddress(e.target.value)}
            placeholder="0x..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleVerifyUser}
            variant="contained"
            color={verifyAction ? 'success' : 'warning'}
            disabled={verifyLoading || !verifyAddress.trim()}
          >
            {verifyLoading ? 'Processing...' : (verifyAction ? 'Verify' : 'Unverify')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carbon Price Dialog */}
      <Dialog open={updateCarbonPriceDialogOpen} onClose={() => setUpdateCarbonPriceDialogOpen(false)}>
        <DialogTitle>Update Carbon Price</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Carbon Price (AUD)"
            type="number"
            fullWidth
            value={carbonPriceInput}
            onChange={(e) => setCarbonPriceInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateCarbonPriceDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateCarbonPriceConfirm}
            variant="contained"
            color="info"
            disabled={!carbonPriceInput}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ergon Price Dialog */}
      <Dialog open={updateErgonPriceDialogOpen} onClose={() => setUpdateErgonPriceDialogOpen(false)}>
        <DialogTitle>Update Ergon Price</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Ergon Price (AUD)"
            type="number"
            fullWidth
            value={ergonPriceInput}
            onChange={(e) => setErgonPriceInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateErgonPriceDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateErgonPriceConfirm}
            variant="contained"
            color="warning"
            disabled={!ergonPriceInput}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* KYC Application Detail Dialog */}
      <Dialog 
        open={applicationDialogOpen} 
        onClose={() => setApplicationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment color="primary" />
              <Typography variant="h6">KYC Application Details</Typography>
            </Box>
            {selectedApplication && (
              <Chip
                label={selectedApplication.status.toUpperCase()}
                color={getStatusColor(selectedApplication.status)}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ minHeight: 400 }}>
          {loadingApplication ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <LinearProgress sx={{ width: '100%' }} />
              <Typography sx={{ ml: 2 }}>Loading application data...</Typography>
            </Box>
          ) : applicationData ? (
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Full Name</Typography>
                <Typography variant="body1">
                  {(applicationData.firstName || "")} {(applicationData.middleName || "")} {(applicationData.lastName || "")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
                <Typography variant="body1">{applicationData.dateOfBirth || ""}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Nationality</Typography>
                <Typography variant="body1">{applicationData.nationality || ""}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Gender</Typography>
                <Typography variant="body1">{applicationData.gender || 'Not specified'}</Typography>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{applicationData.email || ""}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Phone</Typography>
                <Typography variant="body1">{applicationData.phone || ""}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Address</Typography>
                <Typography variant="body1">
                  {(applicationData.address?.street || "")}, {(applicationData.address?.suburb || "")}<br />
                  {(applicationData.address?.state || "")} {(applicationData.address?.postcode || "")}, {(applicationData.address?.country || "")}
                </Typography>
              </Grid>

              {/* Identity Documents */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Identity Documents
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              {applicationData.primaryId?.type && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>Primary ID Document</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Type</Typography>
                          <Typography variant="body1">{applicationData.primaryId?.type || ""}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Number</Typography>
                          <Typography variant="body1">{applicationData.primaryId?.number || ""}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Expiry Date</Typography>
                          <Typography variant="body1">{applicationData.primaryId?.expiryDate || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Issuing Authority</Typography>
                          <Typography variant="body1">{applicationData.primaryId?.issuingAuthority || 'N/A'}</Typography>
                        </Grid>
                        {applicationData.primaryId?.ipfsHash && (
                          <Grid item xs={12}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Download />}
                              onClick={() => window.open(loadApplicationDocument(applicationData.primaryId.ipfsHash), '_blank')}
                            >
                              View Document
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Additional Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Occupation</Typography>
                <Typography variant="body1">{applicationData.occupation || ""}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Source of Funds</Typography>
                <Typography variant="body1">{applicationData.sourceOfFunds || ""}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Purpose of Account</Typography>
                <Typography variant="body1">{applicationData.purposeOfAccount || ""}</Typography>
              </Grid>

              {/* Application Details */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Application Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Application ID</Typography>
                <Typography variant="body1" fontFamily="monospace">{applicationData.applicationId || ""}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Submission Date</Typography>
                <Typography variant="body1">{applicationData.submissionDate ? new Date(applicationData.submissionDate).toLocaleString() : ""}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Wallet Address</Typography>
                <Typography variant="body1" fontFamily="monospace">{applicationData.walletAddress || ""}</Typography>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="error">
              Failed to load application data
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApplicationDialogOpen(false)}>
            Close
          </Button>
          {selectedApplication?.status === 'pending' && !loadingApplication && (
            <>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleRejectApplication(selectedApplication.applicationId, 'Insufficient documentation')}
                startIcon={<Close />}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApproveApplication(selectedApplication.applicationId, selectedApplication.walletAddress)}
                startIcon={<CheckCircle />}
              >
                Approve & Verify
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPlatformManagement;
